import { FunctionComponent } from 'react';
import { Helmet } from 'react-helmet';
import styled from 'styled-components';
import {
  Page,
  Main,
  Title,
  Heading,
  Subheading,
  Paragraph,
} from '../../components';

const EmailLukas = styled('span')({
  '&::before': {
    content: "'lr058@hdm-s'",
  },
  '&::after': {
    content: "'tuttgart.de'",
  },
});

const EmailFabian = styled('span')({
  '&::before': {
    content: "'fh102@hdm-s'",
  },
  '&::after': {
    content: "'tuttgart.de'",
  },
});

const View: FunctionComponent = () => (
  <Page>
    <Helmet>
      <title>Privacy | StudentsTinder</title>
      <meta name="robots" content="noindex" />
    </Helmet>

    <Main contentType="text">
      <Title>Privacy</Title>

      <Paragraph>
        StudentsTinder is a non-commercial project by students at the Hochschule
        der Medien in Stuttgart.
      </Paragraph>

      <Heading>1. An overview of data protection</Heading>

      <Subheading>General</Subheading>
      <Paragraph>
        The following gives a simple overview of what happens to your personal
        information when you visit our website. Personal information is any data
        with which you could be personally identified. Detailed information on
        the subject of data protection can be found in our privacy policy found
        below.
      </Paragraph>
      <Subheading>
        Who is responsible for the data collection on this website?
      </Subheading>
      <Paragraph>
        The data collected on this website are processed by the website
        operator. The operator&apos;s contact details can be found in the
        website&apos;s required legal notice.
      </Paragraph>
      <Subheading>How do we collect your data?</Subheading>
      <Paragraph>
        Some data are collected when you provide it to us. This could, for
        example, be data you enter on a contact form.
      </Paragraph>
      <Paragraph>
        Other data are collected automatically by our IT systems when you visit
        the website. These data are primarily technical data such as the browser
        and operating system you are using or when you accessed the page. These
        data are collected automatically as soon as you enter our website.
      </Paragraph>
      <Subheading>What do we use your data for?</Subheading>
      <Paragraph>
        The data is collected to ensure the proper functioning of the website
        and software.
      </Paragraph>
      <Subheading>What rights do you have regarding your data?</Subheading>
      <Paragraph>
        You always have the right to request information about your stored data,
        its origin, its recipients, and the purpose of its collection at no
        charge. You also have the right to request that it be corrected,
        blocked, or deleted. You can contact us at any time using the address
        given in the legal notice if you have further questions about the issue
        of privacy and data protection. You may also, of course, lodge a
        complaint with the competent regulatory authorities.
      </Paragraph>

      <Heading>2. General information and mandatory information</Heading>
      <Subheading>Data protection</Subheading>
      <Paragraph>
        The operators of this website take the protection of your personal data
        very seriously. We treat your personal data as confidentially and in
        accordance with the statutory data protection regulations and this
        privacy policy.
      </Paragraph>
      <Paragraph>
        If you use this website, various pieces of personal data will be
        collected. Personal information is any data with which you could be
        personally identified. This privacy policy explains what information we
        collect and what we use it for. It also explains how and for what
        purpose this happens.
      </Paragraph>
      <Paragraph>
        Please note that data transmitted via the internet (e.g. via email
        communication) may be subject to security breaches. Complete protection
        of your data from third-party access is not possible.
      </Paragraph>

      <Subheading>
        Notice concerning the party responsible for this website
      </Subheading>
      <Paragraph>
        The party responsible for processing data on this website is: Lukas
        Rosenke, Fabian Hiller and Daniel Heinemann, Heerfeldstraße 17, 71570
        Oppenweiler, Germany, Email: <EmailLukas /> or <EmailFabian />,
      </Paragraph>
      <Paragraph>
        The responsible party is the natural or legal person who alone or
        jointly with others decides on the purposes and means of processing
        personal data (names, email addresses, etc.).
      </Paragraph>

      <Subheading>
        Revocation of your consent to the processing of your data
      </Subheading>
      <Paragraph>
        Many data processing operations are only possible with your express
        consent. You may revoke your consent at any time with future effect. An
        informal email making this request is sufficient. The data processed
        before we receive your request may still be legally processed.
      </Paragraph>

      <Subheading>
        Right to lodge complaints with regulatory authorities
      </Subheading>
      <Paragraph>
        If there has been a breach of data protection legislation, the person
        affected may lodge a complaint with the competent regulatory
        authorities. The competent regulatory authority for matters related to
        data protection legislation is the data protection officer of the German
        state in which our company is headquartered. A list of data protection
        officer and their contact details can be found at the following link:{' '}
        <a href="https://www.bfdi.bund.de/DE/Infothek/Anschriften_Links/anschriften_links-node.html">
          https://www.bfdi.bund.de/DE/Infothek/Anschriften_Links/anschriften_links-node.html
        </a>
        .
      </Paragraph>

      <Subheading>Right to data portability</Subheading>
      <Paragraph>
        You have the right to have data which we process based on your consent
        or in fulfilment of a contract automatically delivered to yourself or to
        a third party in a standard, machine-readable format.
      </Paragraph>

      <Subheading>SSL or TLS encryption</Subheading>
      <Paragraph>
        This site uses SSL or TLS encryption for security reasons and for the
        protection of the transmission of confidential content, such as the
        inquiries you send to us as the site operator. You can recognize an
        encrypted connection in your browser&apos;s address line when it changes
        from &quot;http://&quot; to &quot;https://&quot; and the lock icon is
        displayed in your browser&apos;s address bar.
      </Paragraph>
      <Paragraph>
        If SSL or TLS encryption is activated, the data you transfer to us
        cannot be read by third parties.
      </Paragraph>

      <Subheading>Information, blocking, deletion</Subheading>
      <Paragraph>
        As permitted by law, you have the right to be provided at any time with
        information free of charge about any of your personal data that is
        stored as well as its origin, the recipient and the purpose for which it
        has been processed. You also have the right to have this data corrected,
        blocked or deleted. You can contact us at any time using the address
        given in our legal notice if you have further questions on the topic of
        personal data.
      </Paragraph>

      <Heading>3. Data collection on our website</Heading>

      {/* <Subheading>Cookies</Subheading>
      <Paragraph>
        Some of our web pages use cookies. Cookies do not harm your computer and
        do not contain any viruses. Cookies help make our website more
        user-friendly, efkcient, and secure. Cookies are small text kles that
        are stored on your computer and saved by your browser.
      </Paragraph>
      <Paragraph>
        Most of the cookies we use are so-called &quot;session cookies.&quot;
        They are automatically deleted after your visit. Other cookies remain in
        your device&apos;s memory until you delete them. These cookies make it
        possible to recognize your browser when you next visit the site.
      </Paragraph>
      <Paragraph>
        You can configure your browser to inform you about the use of cookies so
        that you can decide on a case-by-case basis whether to accept or reject
        a cookie. Alternatively, your browser can be configured to automatically
        accept cookies under certain conditions or to always reject them, or to
        automatically delete cookies when closing your browser. Disabling
        cookies may limit the functionality of this website.
      </Paragraph>
      <Paragraph>
        Cookies which are necessary to allow electronic communications or to
        provide certain functions you wish to use (such as the shopping cart)
        are stored pursuant to Art. 6 paragraph 1, letter f of DSGVO. The
        website operator has a legitimate interest in the storage of cookies to
        ensure an optimized service provided free of technical errors. If other
        cookies (such as those used to analyse your surfing behavior) are also
        stored, they will be treated separately in this privacy policy.
      </Paragraph> */}

      <Subheading>Server log files</Subheading>
      <Paragraph>
        The website provider automatically collects and stores information that
        your browser automatically transmits to us in &quot;server log
        files&quot;. These are:
      </Paragraph>
      <Paragraph>
        - Browser type and browser version operating system used
        <br />- Referrer URL
        <br />- Host name of the accessing computer time of the server request
        <br />- IP address
      </Paragraph>
      <Paragraph>
        These data will not be combined with data from other sources.
      </Paragraph>
      <Paragraph>
        The basis for data processing is Art. 6 (1) (b) DSGVO, which allows the
        processing of data to fulkll a contract or for measures preliminary to a
        contract.
      </Paragraph>

      <Subheading>Google Firebase</Subheading>
      <Paragraph>
        We use Google Firebase, a service of Google Inc., 1600 Amphitheater
        Parkway, Mountain View, CA 94043, USA (&quot;Firebase&quot;) in our
        website and apps. Your personal data will be processed and stored with
        exception of the authentication data within the member states of the
        European Union. The authentication data is processed and stored in the
        United States. However, Google Inc. is committed to complying with EU
        privacy standards.
      </Paragraph>
      <Paragraph>
        For more information, see Google&apos;s Terms of Use and Privacy Policy.
        These can be found at{' '}
        <a href="https://policies.google.com/privacy">
          https://policies.google.com/privacy
        </a>{' '}
        and{' '}
        <a href="https://policies.google.com/terms">
          https://policies.google.com/terms
        </a>
        .
      </Paragraph>
    </Main>
  </Page>
);

export default View;
